import axios from 'axios'

const API_BASE_URL = 'https://anatil.xsada.net/api'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
})

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Token ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export const attendanceApi = {
  // Record single attendance
  recordAttendance(data) {
    return apiClient.post('/attendance/record', data)
  },

  // Record bulk attendance
  bulkRecordAttendance(data) {
    return apiClient.post('/attendance/bulk-record', data)
  },

  // Get attendance by date
  getAttendanceByDate(date) {
    return apiClient.get(`/attendance/date/${date}`)
  },

  // Get student attendance history
  getStudentAttendance(studentId) {
    return apiClient.get(`/attendance/student/${studentId}`)
  },

  // Get class attendance
  getClassAttendance(classId) {
    return apiClient.get(`/attendance/class/${classId}`)
  },

  // Get attendance statistics
  getAttendanceStatistics(params = {}) {
    return apiClient.get('/attendance/statistics', { params })
  },

  // Get all attendance records (CRUD)
  getAllAttendance() {
    return apiClient.get('/attendance/')
  },

  // Get single attendance record
  getAttendance(id) {
    return apiClient.get(`/attendance/${id}/`)
  },

  // Update attendance record
  updateAttendance(id, data) {
    return apiClient.put(`/attendance/${id}/`, data)
  },

  // Delete attendance record
  deleteAttendance(id) {
    return apiClient.delete(`/attendance/${id}/`)
  },

  // Get students list
  getStudents() {
    return apiClient.get('/oqughuchi/')
  },

  // Get teachers list
  getTeachers() {
    return apiClient.get('/oqutquchi/')
  },

  // Get classes list
  getClasses() {
    return apiClient.get('/sinip/')
  },

  // Get teacher's students (optionally filtered by class_id)
  getTeacherStudents(classId = null) {
    const params = classId ? { class_id: classId } : {}
    return apiClient.get('/teacher/students/', { params })
  },

  // Get teacher's classes
  getTeacherClasses() {
    return apiClient.get('/teacher/classes/')
  },

  // Get teacher's attendance records
  getTeacherAttendance() {
    return apiClient.get('/teacher/attendance/')
  },

  // Get teacher's attendance records by date
  getTeacherAttendanceByDate(date) {
    return apiClient.get(`/teacher/attendance/${date}`)
  },

  // Get teacher's attendance statistics
  getTeacherAttendanceStatistics(params = {}) {
    return apiClient.get('/teacher/attendance-statistics/', { params })
  },

  // Create student
  createStudent(data) {
    return apiClient.post('/teacher/students/', data)
  },

  // Update student
  updateStudent(id, data) {
    return apiClient.put(`/teacher/students/${id}/`, data)
  },

  // Delete student
  deleteStudent(id) {
    return apiClient.delete(`/teacher/students/${id}/`)
  },

  // Homework Management API
  // Record single homework
  recordHomework(data) {
    return apiClient.post('/homework/record', data)
  },

  // Record bulk homework
  bulkRecordHomework(data) {
    return apiClient.post('/homework/bulk-record', data)
  },

  // Get homework by date
  getHomeworkByDate(date) {
    return apiClient.get(`/homework/date/${date}`)
  },

  // Get student homework history
  getStudentHomework(studentId) {
    return apiClient.get(`/homework/student/${studentId}`)
  },

  // Get class homework
  getClassHomework(classId) {
    return apiClient.get(`/homework/class/${classId}`)
  },

  // Get all homework records (CRUD)
  getAllHomework() {
    return apiClient.get('/homework/')
  },

  // Get single homework record
  getHomework(id) {
    return apiClient.get(`/homework/${id}/`)
  },

  // Update homework record
  updateHomework(id, data) {
    return apiClient.put(`/homework/${id}/`, data)
  },

  // Delete homework record
  deleteHomework(id) {
    return apiClient.delete(`/homework/${id}/`)
  },

  // Get teacher's homework records
  getTeacherHomework() {
    return apiClient.get('/teacher/homework/')
  },

  // Get teacher's homework records by date
  getTeacherHomeworkByDate(date) {
    return apiClient.get(`/teacher/homework/${date}`)
  },

  // Get teacher profile with role
  getTeacherProfile() {
    return apiClient.get('/teacher/profile/')
  },

  // Get class attendance summary (for grade_manager and manager)
  getClassAttendanceSummary() {
    return apiClient.get('/teacher/class-attendance-summary/')
  },

  // Get class homework summary (for grade_manager and manager)
  getClassHomeworkSummary() {
    return apiClient.get('/teacher/class-homework-summary/')
  },

  // Get dates with attendance records (for calendar highlighting)
  getTeacherAttendanceDates(classId = null) {
    const params = classId ? { class_id: classId } : {}
    return apiClient.get('/teacher/attendance-dates/', { params })
  },

  // Get dates with homework records (for calendar highlighting)
  getTeacherHomeworkDates(classId = null) {
    const params = classId ? { class_id: classId } : {}
    return apiClient.get('/teacher/homework-dates/', { params })
  },

  // Vacation homework
  getTeacherVacationHomework(params = {}) {
    return apiClient.get('/teacher/vacation-homework/', { params })
  },

  bulkRecordVacationHomework(data) {
    return apiClient.post('/teacher/vacation-homework/bulk-record/', data)
  },

  // Exam scores
  getTeacherExamScores(params = {}) {
    return apiClient.get('/teacher/exam-scores/', { params })
  },

  bulkRecordExamScores(data) {
    return apiClient.post('/teacher/exam-scores/bulk-record/', data)
  },

  // Student summary
  getTeacherStudentSummary(params = {}) {
    return apiClient.get('/teacher/student-summary/', { params })
  },

  // School year
  getCurrentSchoolYear() {
    return apiClient.get('/teacher/school-year/')
  },

  finalizeSchoolYear() {
    return apiClient.post('/teacher/school-year/finalize/')
  }
}

export default attendanceApi
